import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Confetti from 'react-confetti'
import { useDarkMode } from '../../context/DarkModeContext'
import { baseURL } from '../../config'
import { getAuthHeaders } from '../../services/authService'

function PaymentSuccess() {
	const { isDarkMode } = useDarkMode()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [isValidating, setIsValidating] = useState(true)
	const [isValid, setIsValid] = useState(false)
	const [planType, setPlanType] = useState('pro')
	const [countdown, setCountdown] = useState(10)

	// Verify payment session and clean URL
	useEffect(() => {
		const sessionId = searchParams.get('session_id')

		if (!sessionId) {
			// No session ID, redirect to loadboard
			navigate('/loadboard', { replace: true })
			return
		}

		// Verify the session with backend
		const verifySession = async () => {
			try {
				const response = await fetch(
					`${baseURL}/api/stripe/verify-payment-session?session_id=${sessionId}`,
					{
						headers: getAuthHeaders(),
					}
				)
				const data = await response.json()

				if (data.success) {
					setIsValid(true)
					setPlanType(data.planType || 'pro')
				} else {
					// Invalid session, redirect to loadboard
					navigate('/loadboard', { replace: true })
				}
			} catch (error) {
				console.error('Payment verification failed:', error)
				// On error, redirect to loadboard
				navigate('/loadboard', { replace: true })
			} finally {
				setIsValidating(false)
			}
		}

		verifySession()
	}, [searchParams, navigate])

	// Countdown timer and redirect
	useEffect(() => {
		if (!isValid) return

		const interval = setInterval(() => {
			setCountdown(prev => {
				if (prev <= 1) {
					clearInterval(interval)
					if (planType.includes('team')) {
						navigate('/settings/team')
					} else {
						navigate('/loadboard')
					}
					return 0
				}
				return prev - 1
			})
		}, 1000)

		return () => clearInterval(interval)
	}, [isValid, planType, navigate])

	// Show loading while validating
	if (isValidating) {
		return (
			<div
				style={{
					minHeight: '100vh',
					backgroundColor: isDarkMode ? '#0D1117' : '#ffffff',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '20px',
				}}
			>
				<div
					style={{
						color: isDarkMode ? '#ffffff' : '#1a1a1a',
						fontSize: '18px',
					}}
				>
					Verifying payment...
				</div>
			</div>
		)
	}

	if (!isValid) return null

	return (
		<div
			style={{
				minHeight: '100vh',
				backgroundColor: isDarkMode ? '#0D1117' : '#ffffff',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '20px',
			}}
		>
			{/* Confetti */}
			<Confetti
				width={window.innerWidth}
				height={window.innerHeight}
				numberOfPieces={400}
				recycle={true}
				gravity={0.2}
				initialVelocityY={20}
				wind={0.01}
				colors={[
					'#ff6b6b',
					'#4ecdc4',
					'#45b7d1',
					'#96ceb4',
					'#ffeaa7',
					'#fd79a8',
					'#a29bfe',
					'#6c5ce7',
					'#00b894',
					'#e17055',
				]}
			/>

			{/* Success Card */}
			<div
				style={{
					backgroundColor: isDarkMode ? '#1c2128' : '#ffffff',
					padding: '60px 40px',
					borderRadius: '16px',
					boxShadow: isDarkMode
						? '0 20px 50px rgba(0, 0, 0, 0.5)'
						: '0 20px 50px rgba(0, 0, 0, 0.15)',
					textAlign: 'center',
					maxWidth: '500px',
					width: '100%',
				}}
			>
				{/* Success Icon */}
				<div
					style={{
						fontSize: '80px',
						marginBottom: '24px',
					}}
				>
					🎉
				</div>

				{/* Title */}
				<h1
					style={{
						fontSize: '32px',
						fontWeight: '700',
						color: isDarkMode ? '#ffffff' : '#1a1a1a',
						marginBottom: '16px',
						lineHeight: '1.2',
					}}
				>
					Payment Successful!
				</h1>

				{/* Subtitle */}
				<p
					style={{
						fontSize: '18px',
						color: isDarkMode ? '#8b949e' : '#6b7280',
						marginBottom: '32px',
						lineHeight: '1.5',
					}}
				>
					Yahoo! Your subscription has been upgraded successfully.
				</p>

				{/* Button */}
				<button
					onClick={() => {
						if (planType.includes('team')) {
							navigate('/settings/team')
						} else {
							navigate('/loadboard')
						}
					}}
					style={{
						backgroundColor: '#10b981',
						color: '#ffffff',
						border: 'none',
						padding: '16px 32px',
						borderRadius: '8px',
						fontSize: '16px',
						fontWeight: '600',
						cursor: 'pointer',
						transition: 'background-color 0.2s ease',
					}}
					onMouseEnter={e => (e.target.style.backgroundColor = '#059669')}
					onMouseLeave={e => (e.target.style.backgroundColor = '#10b981')}
				>
					{planType.includes('team')
						? 'Go to Team Settings'
						: 'Start Dispatching!'}
				</button>

				{/* Auto redirect message */}
				<p
					style={{
						fontSize: '14px',
						color: isDarkMode ? '#6e7681' : '#9ca3af',
						marginTop: '20px',
					}}
				>
					Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
				</p>
			</div>
		</div>
	)
}

export default PaymentSuccess
